import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { parse } from 'qs';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { records as buildsRecords } from '@saucelabs/sl-module-builds';
import { Row, Grid, Col } from '@saucelabs/sl-component-grid';
import JobMetadata from '@saucelabs/sl-component-job-metadata';
import { ContentLoader } from '@saucelabs/sl-component-loader';
import { records as userRecords } from '@saucelabs/sl-module-user';
import ReportIssueModal, { actions as reportIssueActions } from '@saucelabs/sl-container-report-issue-modal';
import NotificationsContainer from '@saucelabs/sl-container-notifications';
import { operations as performanceOperations } from '@saucelabs/sl-module-performance';
import { records as jobRecords } from '@saucelabs/sl-module-jobs';

import Styles from './PerformanceMainContainer.styles';
import PerformanceNav from './../components/PerformanceNav';
import { operations, selectors, actions } from './../ducks/container';
import { operations as jobDetailsOperations } from './../../../web-ui-job-details/src/ducks/container';

// Custom Effect to store previous value,
// which will be used in comparing with old values and make decision for further operations
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current || {};
}

const PerformanceMain = ({
  children,
  match,
  location,
  job,
  loading,
  openReportJobIssueModal,
  onSelectVisibility,
  onRouteParamsChange,
  updateShouldCallInit,
  user,
  build,
}) => {
  const { params: { jobId, pageIndex: defaultPageIndex } } = match;
  const pageIndex = parseInt(defaultPageIndex, 10);
  const isOnHistoryPage = location.pathname.substring(location.pathname.length - 8) === '/history';
  const { search } = location;
  const { auth } = parse(search, { ignoreQueryPrefix: true });
  const prevValue = usePrevious({ jobId, pageIndex, isOnHistoryPage });

  useEffect(() => {
    onRouteParamsChange({ pageIndex, prevValue, jobId, auth });
  }, [jobId, pageIndex]);

  useEffect(() => {
    if (prevValue.isOnHistoryPage !== isOnHistoryPage) {
      updateShouldCallInit(true);
    }
  }, [location.pathname]);

  return (
    <section>
      <ContentLoader
        show={loading}
      >
        <Helmet
          title="Test Performance"
        />
        <PerformanceNav
          isOnHistoryPage={isOnHistoryPage}
          pageIndex={pageIndex}
          routerSearchQuery={search}
          testId={jobId}
        />
        <Grid
          className={Styles.grid}
          tagName="section"
        >
          <div className={`${Styles['performance-card']} card`}>
            <NotificationsContainer />
            <Row className={Styles.wrapper}>
              <Col
                className={Styles['job-metadata-container']}
                xs={12}
              >
                <ReportIssueModal
                  additionalFields={{
                    user: user.get('username'),
                    name: user.displayName(),
                    email: user.get('email'),
                    job_id: jobId,
                  }}
                />
                {job.get('id') && (
                  <JobMetadata
                    build={build}
                    buildId={build.get('id')}
                    job={job}
                    onSelectVisibility={onSelectVisibility}
                    openReportJobIssueModal={openReportJobIssueModal}
                  />
                )}
              </Col>
              <Col
                className={Styles['performance-route-section']}
                xs={12}
              >
                {children}
              </Col>
            </Row>
          </div>
        </Grid>
      </ContentLoader>
    </section>
  );
};

PerformanceMain.propTypes = {
  build: ImmutablePropTypes.recordOf(buildsRecords.BuildRecord).isRequired,
  children: PropTypes.node.isRequired,
  job: ImmutablePropTypes.recordOf(jobRecords.JobRecord).isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  onRouteParamsChange: PropTypes.func.isRequired,
  onSelectVisibility: PropTypes.func,
  openReportJobIssueModal: PropTypes.func.isRequired,
  updateShouldCallInit: PropTypes.func.isRequired,
  user: ImmutablePropTypes.recordOf(userRecords.UserRecordClass).isRequired,
};

PerformanceMain.defaultProps = {
  onSelectVisibility: () => {},
};

const mapStateToProps = selectors.containerSelector;

const mapDispatchToProps = {
  acknowledgeRegimes: performanceOperations.acknowledgeRegimes,
  closeAcknowledgeBanner: performanceOperations.closeAcknowledgeBanner,
  getJob: jobDetailsOperations.getJob,
  onRouteParamsChange: operations.onRouteParamsChange,
  openReportJobIssueModal: reportIssueActions.openReportJobIssueModal,
  updateShouldCallInit: actions.updateShouldCallInit,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(PerformanceMain),
);

export { PerformanceMain };
